import React, { useCallback, useEffect, useState } from 'react';

import {
    Button,
    Modal,
    Space,
    Typography,
    message
} from 'antd';

import { CloudDownloadOutlined } from '@ant-design/icons';

import { isInstallerNativeApp } from '../utils/installerSms';
import { startOfflineQueueWatcher } from '../utils/installerOfflineQueue';

import {
    downloadAndInstallApk,
    fetchRemoteAppUpdate,
    getInstalledAppInfo,
    isUpdateAvailable
} from '../utils/installerUpdate';

const { Text, Paragraph } = Typography;

const InstallerAppUpdateGate = ({
    autoCheck = true
}) => {
    const [visible, setVisible] = useState(false);
    const [busy, setBusy] = useState(false);
    const [localInfo, setLocalInfo] = useState(null);
    const [remote, setRemote] = useState(null);

    const checkForUpdate = useCallback(async () => {
        if (!isInstallerNativeApp()) {
            return;
        }

        try {
            const [installed, update] =
                await Promise.all([
                    getInstalledAppInfo(),
                    fetchRemoteAppUpdate()
                ]);

            setLocalInfo(installed);
            setRemote(update);

            if (
                isUpdateAvailable(installed, update)
            ) {
                setVisible(true);
            }
        } catch (error) {
            console.warn(
                'Installer update check failed',
                error
            );
        }
    }, []);

    useEffect(() => {
        if (isInstallerNativeApp()) {
            startOfflineQueueWatcher();
        }
    }, []);

    useEffect(() => {
        if (!autoCheck) {
            return undefined;
        }

        const timer = setTimeout(() => {
            checkForUpdate();
        }, 800);

        return () => clearTimeout(timer);
    }, [autoCheck, checkForUpdate]);

    const handleUpdate = async () => {
        if (!remote?.apkUrl) {
            message.error('В манифесте нет ссылки на APK');
            return;
        }

        setBusy(true);

        try {
            await downloadAndInstallApk(
                remote.apkUrl
            );
            message.success(
                'Загрузка завершена — подтвердите установку'
            );
        } catch (error) {
            message.error(
                error?.message ||
                    'Не удалось обновить приложение'
            );
        } finally {
            setBusy(false);
        }
    };

    if (!isInstallerNativeApp()) {
        return null;
    }

    return (
        <Modal
            open={visible}
            title="Доступно обновление приложения"
            closable={!remote?.force}
            maskClosable={!remote?.force}
            keyboard={!remote?.force}
            onCancel={() => {
                if (!remote?.force) {
                    setVisible(false);
                }
            }}
            footer={
                <Space>
                    {!remote?.force ? (
                        <Button
                            onClick={() =>
                                setVisible(false)
                            }
                        >
                            Позже
                        </Button>
                    ) : null}
                    <Button
                        type="primary"
                        icon={
                            <CloudDownloadOutlined />
                        }
                        loading={busy}
                        onClick={handleUpdate}
                    >
                        Обновить
                    </Button>
                </Space>
            }
        >
            <Paragraph style={{ marginBottom: 8 }}>
                Установлено:{' '}
                <Text strong>
                    {localInfo?.versionName || '—'}
                    {localInfo?.versionCode != null
                        ? ` (${localInfo.versionCode})`
                        : ''}
                </Text>
            </Paragraph>

            <Paragraph style={{ marginBottom: 8 }}>
                Новая версия:{' '}
                <Text strong>
                    {remote?.versionName || '—'}
                    {remote?.versionCode != null
                        ? ` (${remote.versionCode})`
                        : ''}
                </Text>
            </Paragraph>

            {remote?.notes ? (
                <Paragraph type="secondary">
                    {remote.notes}
                </Paragraph>
            ) : null}
        </Modal>
    );
};

export default InstallerAppUpdateGate;
